import type { ReactNode } from 'react';
import AccessLevelFailed from '@/app/common/components/AccessLevelFailed';
import type { CheckAccessResponse } from '@/app/common/api/types/accessLevelApiTypes';
import type { ApiQueryError } from '@/app/common/utils/apiErrorUtils';
import useToolAccess, {
  type ToolAccessResult,
  type ToolName
} from '@/app/common/hooks/useToolAccess';

interface ToolAccessDeniedProps {
  access: ToolAccessResult;
  tool_label: string;
}

interface ToolAccessGuardProps {
  tool_name: ToolName;
  tool_label: string;
  children: ReactNode;
  loadingFallback?: ReactNode;
}

const deniedAccessData = (
  error: ApiQueryError
): CheckAccessResponse | undefined => {
  if (!error || !('data' in error) || error.data == null) return undefined;
  return error.data as CheckAccessResponse;
};

export const ToolAccessDenied = ({
  access,
  tool_label
}: ToolAccessDeniedProps) => (
  <AccessLevelFailed
    tool_name={tool_label}
    accessData={
      !access.isError ? access.accessData : deniedAccessData(access.error)
    }
    onRetry={() => access.refetch()}
    isRetrying={access.isFetching}
    error={access.error}
  />
);

const ToolAccessGuard = ({
  tool_name,
  tool_label,
  children,
  loadingFallback = null
}: ToolAccessGuardProps) => {
  const access = useToolAccess(tool_name);

  if (access.isLoading) return loadingFallback;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label={tool_label} />;
  }

  return children;
};

export { useToolAccess } from '@/app/common/hooks/useToolAccess';
export default ToolAccessGuard;
